import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useNavigate, useLocation } from "react-router-dom";
import ProfileImg from "../../src/assets/images/logo.png";

const apiUrl = process.env.REACT_APP_API_URL;

// Function to clear access token and redirect to dashboard
const handleLogoutRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const confirmation = urlParams.get("confirmation");
  console.log(confirmation);
  if (confirmation === "logout_success") {
    localStorage.removeItem("accessToken");
    window.location.href = "/";
  }
};

// Call the function on page load
window.onload = handleLogoutRedirect;

const FreshSidebar = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(0);
  const [activePath, setActivePath] = useState("/dashboard");
  const [activeSubmenuItem, setActiveSubmenuItem] = useState(null);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [renewalRole, setRenewalRole] = useState("");

  const isMobile = () => window.innerWidth <= 768;
  const [isExpanded, setIsExpanded] = useState(!isMobile());

  // Create menu items based on renewal role
  const getMenuItems = (renewalRole) => {
    const baseItems = [
      {
        name: "Dashboard",
        icon: "bi-house-door",
        path: "/renewals-dashboard",
      },
    ];

    // Role 1: Show all menu items
    if (renewalRole === "1" || renewalRole === 1) {
      return [
        ...baseItems,
        {
          name: "Manage Permissions",
          icon: "bi-shield-check",
          path: "/renewals-manage-roles",
        },
        {
          name: "Renewal Logs",
          icon: "bi-arrow-clockwise",
          path: "/renewals-logs",
        },
        {
          name: "Renewal Risk Flag",
          icon: "bi-shield-exclamation",
          path: "/renewals-list-risk-flag",
        },
        {
          name: "Settings",
          icon: "bi-gear",
          path: "/settings",
          submenu: [
            { name: "Expiration Buffer", path: "/renewals-expiration-days" },
            { name: "Data Points", path: "/renewals-datapoint-settings" },
            {
              name: "State-Based NBD Buffer",
              path: "/renewals-state-non-renewal-days",
            },
            {
              name: "Global Notification Settings",
              path: "/renewals-notification-settings",
            },
          ],
        },
      ];
    }

    // Role 2: Limited menu items
    if (renewalRole === "2" || renewalRole === 2) {
      return [
        ...baseItems,
        {
          name: "Renewal Logs",
          icon: "bi-arrow-clockwise",
          path: "/renewals-logs",
        },
        {
          name: "Settings",
          icon: "bi-gear",
          path: "/settings",
          submenu: [
            {
              name: "Notification Settings",
              path: "/renewals-notification-settings",
            },
          ],
        },
      ];
    }

    // Default: return base items only
    return baseItems;
  };

  const [menuItems, setMenuItems] = useState([]);

  const logoutItem = [
    {
      name: "Log Out",
      icon: "bi-box-arrow-right",
      path: "/logout",
    },
  ];

  // Update active states based on current URL
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setRenewalRole(user.RenewalRole);
      setMenuItems(getMenuItems(user.RenewalRole));
    }
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      // Find the Settings menu item index in the current menu
      const settingsIndex = menuItems.findIndex(
        (item) => item.name === "Settings"
      );
      if (settingsIndex !== -1) {
        setOpenSubmenus((prev) => ({ ...prev, [settingsIndex]: true }));
        setIsExpanded(true); // Ensure sidebar is expanded to show submenu
      }
    };

    // Listen for the custom event
    window.addEventListener("openSettingsSubmenu", handleOpenSettings);

    // Cleanup
    return () => {
      window.removeEventListener("openSettingsSubmenu", handleOpenSettings);
    };
  }, [menuItems]);

  useEffect(() => {
    const currentPath = location.pathname;
    let found = false;

    // Find which menu item matches the current path
    menuItems.forEach((item, index) => {
      if (item.submenu) {
        item.submenu.forEach((subItem, subIndex) => {
          if (currentPath.startsWith(subItem.path)) {
            setActivePath(subItem.path);
            setActiveSubmenuItem(`${index}-${subIndex}`);
            setOpenSubmenus((prev) => ({ ...prev, [index]: true }));
            found = true;
          }
        });
      } else if (currentPath === item.path) {
        setActivePath(item.path);
        setActiveSubmenuItem(null);
        setOpenSubmenus({}); // Close all submenus
        found = true;
      }
    });

    // If no match found, reset states
    if (!found) {
      setActiveSubmenuItem(null);
      setOpenSubmenus({});
    }
  }, [location.pathname, menuItems]);

  const handleLogout = () => {
    window.location.href = `${apiUrl}logout?userName=${encodeURIComponent(
      userName
    )}&email=${encodeURIComponent(email)}`;
  };

  const handleItemClick = (index, item) => {
    if (item.submenu) {
      // Toggle submenu
      setOpenSubmenus((prev) => {
        const newState = { ...prev };
        // Close all other submenus
        Object.keys(newState).forEach((key) => {
          if (key !== index.toString()) {
            newState[key] = false;
          }
        });
        // Toggle clicked submenu
        newState[index] = !prev[index];
        return newState;
      });
    } else if (item.path === "/logout") {
      handleLogout();
    } else if (item.path) {
      setActivePath(item.path);
      setActiveSubmenuItem(null);
      setOpenSubmenus({}); // Close all submenus
      navigate(item.path);
    }

    if (isMobile()) {
      setIsExpanded(false);
    }
  };

  const handleSubmenuClick = (mainIndex, subItem, subIndex, e) => {
    e.stopPropagation(); // prevent parent toggle
    setActivePath(subItem.path);
    setActiveSubmenuItem(`${mainIndex}-${subIndex}`);
    // Keep only the parent submenu open
    setOpenSubmenus({ [mainIndex]: true });
    navigate(subItem.path);

    if (isMobile()) {
      setIsExpanded(false);
    }
  };

  const renderMenuItem = (item, index) => {
    const isActive =
      activePath === item.path ||
      (item.submenu && item.submenu.some((sub) => sub.path === activePath));
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isSubmenuOpen = openSubmenus[index];

    return (
      <li key={index} className={`menu-item ${isActive ? "active" : ""}`}>
        <div
          className="menu-item-content"
          onClick={() => handleItemClick(index, item)}
        >
          <i className={`bi ${item.icon} menu-item-icon`}></i>
          {isExpanded && <span className="menu-item-name">{item.name}</span>}
          {hasSubmenu && isExpanded && (
            <i
              className={`bi bi-chevron-down submenu-indicator ${
                isSubmenuOpen ? "open" : ""
              }`}
            ></i>
          )}
        </div>
        {hasSubmenu && isSubmenuOpen && isExpanded && (
          <ul className="submenu">
            {item.submenu.map((subItem, subIndex) => (
              <li
                key={subIndex}
                className={`submenu-item ${
                  activePath === subItem.path ? "active" : ""
                }`}
                onClick={(e) => handleSubmenuClick(index, subItem, subIndex, e)}
              >
                {subItem.name}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const renderLogoutItem = (item, index) => {
    return (
      <li key={index} className="menu-item">
        <div
          className="menu-item-content"
          onClick={() => handleItemClick(index, item)}
        >
          <i className={`bi ${item.icon} menu-item-icon`}></i>
          {isExpanded && <span className="menu-item-name">{item.name}</span>}
        </div>
      </li>
    );
  };

  return (
    <nav className={`sidebar ${isExpanded ? "expanded" : ""}`}>
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <button
          className="toggle-btn"
          onClick={() => {
            setIsExpanded(!isExpanded);
            if (toggleSidebar) {
              toggleSidebar();
            }
          }}
        >
          <i className={`bi fs-4 ${isExpanded ? "bi-list" : "bi-list"}`}></i>
        </button>
      </div>

      {/* Profile Area - Only show when expanded */}
      {isExpanded && (
        <div className="profile-area">
          <img src={ProfileImg} alt="Profile" className="profile-image" />
          <h2 className="profile-name">{userName}</h2>
        </div>
      )}

      {/* Main Menu */}
      <ul className="menu-list">
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </ul>

      {/* Logout */}
      <ul className="logout-list">
        {logoutItem.map((item, index) => renderLogoutItem(item, index))}
      </ul>
    </nav>
  );
};

export default FreshSidebar;
